import { useEffect, useRef } from 'react';
import type { CustomerNotification } from '@/lib/supabase';

interface NotificationPanelProps {
  notifications: CustomerNotification[];
  onClose: () => void;
  onMarkAllRead: () => void;
  onMarkOneRead: (id: string) => void;
}

const NOTIF_ICON: Record<CustomerNotification['type'], { icon: string; bg: string; color: string }> = {
  order_confirmed: { icon: 'ri-checkbox-circle-fill', bg: 'bg-sky-50',   color: 'text-sky-500' },
  order_shipped:   { icon: 'ri-truck-fill',           bg: 'bg-indigo-50', color: 'text-indigo-500' },
  order_delivered: { icon: 'ri-check-double-fill',    bg: 'bg-green-50',  color: 'text-green-500' },
  order_cancelled: { icon: 'ri-close-circle-fill',    bg: 'bg-red-50',    color: 'text-red-500' },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function NotificationPanel({ notifications, onClose, onMarkAllRead, onMarkOneRead }: NotificationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-2xl border border-gray-200 shadow-xl z-50 overflow-hidden animate-slide-down"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <i className="ri-notification-3-line text-gray-700"></i>
          <span className="text-gray-900 font-bold text-sm">Notifications</span>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllRead}
              className="text-xs text-gray-500 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-600 text-xs"></i>
          </button>
        </div>
      </div>

      {/* List */}
      <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center">
              <i className="ri-notification-off-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-gray-400 text-sm">No notifications yet</p>
          </div>
        ) : (
          notifications.map(n => {
            const cfg = NOTIF_ICON[n.type];
            return (
              <div
                key={n.id}
                onClick={() => !n.is_read && onMarkOneRead(n.id)}
                className={`flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer hover:bg-gray-50 ${!n.is_read ? 'bg-orange-50/40' : ''}`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${cfg.bg}`}>
                  <i className={`${cfg.icon} ${cfg.color}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-sm font-semibold truncate ${!n.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                      {n.title}
                    </p>
                    {!n.is_read && (
                      <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0"></span>
                    )}
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed mt-0.5 line-clamp-2">{n.message}</p>
                  <p className="text-gray-400 text-[10px] mt-1">{timeAgo(n.created_at)}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
